import React, { useState, useRef, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { AIService, ChatMessage } from '../services/ai.service';
import ChatMessageComponent from '../components/ui/ChatMessage';
import { motion } from 'framer-motion';
import { FadeIn, SlideIn } from '../components/animations';
import { useAuth } from '../contexts/AuthContext';

// Flag to control whether to use the actual API or simulated responses
// Set to true now that we have the backend configured
const USE_ACTUAL_API = true;

// Simulated AI responses for testing the UI without the backend
const SIMULATED_RESPONSES = [
  "ICHRAs (Individual Coverage Health Reimbursement Arrangements) allow employers to provide tax-free reimbursements to employees for individual health insurance premiums and qualified medical expenses. They were established by federal regulations that took effect on January 1, 2020.",
  
  "An ICHRA offers several advantages over traditional group health plans. These include cost predictability for employers, greater plan choice for employees, and potential tax savings for both. Employers can set defined contribution amounts and employees can choose coverage that best meets their needs.",
  
  "To implement an ICHRA, employers should follow these steps: 1) Set employee classes and contribution amounts, 2) Create plan documents, 3) Establish a reimbursement process, 4) Communicate the benefit to employees, and 5) Provide resources for employees to shop for individual coverage.",
  
  "The key regulatory requirements for ICHRAs include: offering the same terms to all employees within a class, providing a 90-day notice to employees before the start of the plan year, ensuring employees have minimum essential coverage, and verifying that coverage remains in effect.",
  
  "When transitioning from a group plan to an ICHRA, employers should provide clear communication about the change, help employees understand how to shop for individual coverage, consider timing the transition during open enrollment, and ensure compliance with notice requirements."
];

// Starting prompt cards to help users know what to ask
const PROMPT_CARDS = [
  {
    category: "ICHRA Basics",
    description: "Learn the fundamentals",
    icon: (
      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
        <path d="M10.394 2.08a1 1 0 00-.788 0l-7 3a1 1 0 000 1.84L5.25 8.051a.999.999 0 01.356-.257l4-1.714a1 1 0 11.788 1.838L7.667 9.088l1.94.831a1 1 0 00.787 0l7-3a1 1 0 000-1.838l-7-3zM3.31 9.397L5 10.12v4.102a8.969 8.969 0 00-1.05-.174 1 1 0 01-.89-.89 11.115 11.115 0 01.25-3.762zM9.3 16.573A9.026 9.026 0 007 14.935v-3.957l1.818.78a3 3 0 002.364 0l5.508-2.361a11.026 11.026 0 01.25 3.762 1 1 0 01-.89.89 8.968 8.968 0 00-5.35 2.524 1 1 0 01-1.4 0zM6 18a1 1 0 001-1v-2.065a8.935 8.935 0 00-2-.712V17a1 1 0 001 1z" />
      </svg>
    ),
    prompts: [
      "What is an ICHRA and how does it work?",
      "What are the key benefits of an ICHRA for employers?"
    ]
  },
  {
    category: "Implementation",
    description: "Practical guidance",
    icon: (
      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
        <path d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z" />
        <path fillRule="evenodd" d="M4 5a2 2 0 012-2 3 3 0 003 3h2a3 3 0 003-3 2 2 0 012 2v11a2 2 0 01-2 2H6a2 2 0 01-2-2V5zm3 4a1 1 0 000 2h.01a1 1 0 100-2H7zm3 0a1 1 0 000 2h3a1 1 0 100-2h-3zm-3 4a1 1 0 100 2h.01a1 1 0 100-2H7zm3 0a1 1 0 100 2h3a1 1 0 100-2h-3z" clipRule="evenodd" />
      </svg>
    ),
    prompts: [
      "What steps should I take to implement an ICHRA?",
      "How should I communicate ICHRA benefits to employees?"
    ]
  },
  {
    category: "Regulations",
    description: "Stay compliant",
    icon: (
      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
        <path fillRule="evenodd" d="M10 1.944A11.954 11.954 0 012.166 5C2.056 5.649 2 6.319 2 7c0 5.225 3.34 9.67 8 11.317C14.66 16.67 18 12.225 18 7c0-.682-.057-1.35-.166-2.001A11.954 11.954 0 0110 1.944zM11 14a1 1 0 11-2 0 1 1 0 012 0zm0-7a1 1 0 10-2 0v3a1 1 0 102 0V7z" clipRule="evenodd" />
      </svg>
    ),
    prompts: [
      "What are the key regulatory requirements for ICHRAs?",
      "How do ICHRAs comply with ACA requirements?"
    ]
  },
  {
    category: "Cost Analysis",
    description: "Understand savings",
    icon: (
      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
        <path fillRule="evenodd" d="M4 4a2 2 0 00-2 2v4a2 2 0 002 2V6h10a2 2 0 00-2-2H4zm2 6a2 2 0 012-2h8a2 2 0 012 2v4a2 2 0 01-2 2H8a2 2 0 01-2-2v-4zm6 4a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
      </svg>
    ),
    prompts: [
      "How can an ICHRA save money for employers?",
      "What factors affect ICHRA allowance amounts?"
    ]
  }
];

// Reference documents that can be opened
const REFERENCE_DOCUMENTS = [
  {
    name: "ICHRA Affordability One Pager 2025",
    description: "Essential affordability guidelines and calculations for 2025",
    filename: "ICHRA_Affordability_One_Pager_2025.pdf"
  },
  {
    name: "ICHRA COBRA Integration",
    description: "How ICHRA works with COBRA continuation coverage",
    filename: "ICHRA_COBRA.pdf"
  },
  {
    name: "Medicare Eligible Employees",
    description: "Special considerations for Medicare-eligible employees in ICHRA",
    filename: "ICHRA_Medicare Eligible Employees.pdf"
  },
  {
    name: "Section 125 Termination Guidance",
    description: "Guidelines for terminating Section 125 plans when implementing ICHRA",
    filename: "ICHRA_Section125_Termination_Guidance.pdf"
  },
  {
    name: "Self-Employed Considerations",
    description: "ICHRA rules and considerations for self-employed individuals",
    filename: "ICHRA_Self-Employed_Considerations.pdf"
  }
];

// API configuration - same as other pages
const API_KEY = '4ws9KDIWIW11u8mNVP0Th2bGN3GhlnnZlquHiv8b';
const API_URL = process.env.REACT_APP_STAGING_API_URL!;

const KnowledgeCenter: React.FC = () => {
  // Auth context for API calls
  const { getIdToken } = useAuth();
  
  // React Router location for navigation detection
  const location = useLocation();
  
  // State for chat conversation history
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  
  // State for loading indicator
  const [isLoading, setIsLoading] = useState(false);
  
  // State for error messages
  const [error, setError] = useState<string | null>(null);
  
  // State for input message
  const [inputMessage, setInputMessage] = useState('');
  
  // State for showing prompt cards
  const [showPrompts, setShowPrompts] = useState(true);
  
  // State for showing frequently referenced documents
  const [showDocuments, setShowDocuments] = useState(false);
  
  // Check for hash navigation to auto-expand documents
  useEffect(() => {
    if (window.location.hash === '#documents') {
      setShowDocuments(true);
      // Scroll to documents section after a brief delay
      setTimeout(() => {
        const documentsSection = document.getElementById('documents-section');
        if (documentsSection) {
          documentsSection.scrollIntoView({ behavior: 'smooth' });
        }
      }, 300);
    }

    // Listen for custom event to expand documents
    const handleExpandDocuments = () => {
      setShowDocuments(true);
      setTimeout(() => {
        const documentsSection = document.getElementById('documents-section');
        if (documentsSection) {
          documentsSection.scrollIntoView({ behavior: 'smooth' });
        }
      }, 300);
    };

    window.addEventListener('expandDocuments', handleExpandDocuments);
    
    return () => {
      window.removeEventListener('expandDocuments', handleExpandDocuments);
    };
  }, []);

  // Additional effect to handle navigation from Quick Actions
  useEffect(() => {
    const checkAndExpandDocuments = () => {
      const urlParams = new URLSearchParams(window.location.search);
      const fromQuickActions = urlParams.get('from') === 'quick-actions';
      const hasDocumentsHash = window.location.hash === '#documents';
      
      console.log('Knowledge Center navigation check:', { 
        fromQuickActions, 
        hasDocumentsHash,
        hash: window.location.hash, 
        search: window.location.search,
        fullURL: window.location.href
      });
      
      if (fromQuickActions || hasDocumentsHash) {
        console.log('Auto-expanding documents section');
        setShowDocuments(true);
        
        // Remove the query parameter to clean up URL but keep the hash
        if (fromQuickActions) {
          window.history.replaceState({}, '', '/knowledge-center#documents');
        }
        
        // Scroll to documents section after animation completes
        setTimeout(() => {
          const documentsSection = document.getElementById('documents-section');
          if (documentsSection) {
            console.log('Scrolling to documents section');
            documentsSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }, 800); // Longer delay to ensure animations complete
      }
    };

    // Check immediately
    checkAndExpandDocuments();
    
    // Also check after a short delay in case React Router hasn't fully updated the URL yet
    const timeoutId = setTimeout(checkAndExpandDocuments, 100);
    
    return () => clearTimeout(timeoutId);
  }, []);

  // Also check on location changes (for React Router navigation)
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const fromQuickActions = urlParams.get('from') === 'quick-actions';
    const hasDocumentsHash = location.hash === '#documents';
    
    console.log('React Router location change detected:', { 
      fromQuickActions, 
      hasDocumentsHash,
      pathname: location.pathname,
      search: location.search,
      hash: location.hash
    });
    
    if (fromQuickActions || hasDocumentsHash) {
      console.log('Expanding documents due to location change');
      setShowDocuments(true);
      
      // Scroll after a delay to ensure component is rendered
      setTimeout(() => {
        const documentsSection = document.getElementById('documents-section');
        if (documentsSection) {
          console.log('Scrolling to documents section from location change');
          documentsSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 600);
    }
  }, [location]);
  
  // Reference for chat container to auto-scroll
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const chatContainerRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to bottom of chat when messages change
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };
  
  useEffect(() => {
    scrollToBottom();
  }, [messages]);
  
  // Get a simulated response for testing without backend
  const getSimulatedResponse = (userMessage: string) => {
    // Choose a random response from the simulated responses
    const randomIndex = Math.floor(Math.random() * SIMULATED_RESPONSES.length);
    return SIMULATED_RESPONSES[randomIndex];
  };
  
  // Handle sending a new message
  const handleSendMessage = async (content: string) => {
    if (!content.trim() || isLoading) return;

    try {
      // Hide prompts once user starts chatting
      setShowPrompts(false);
      
      // Clear any previous errors
      setError(null);
      
      // Add user message to chat
      const userMessage: ChatMessage = { role: 'user', content };
      setMessages(prev => [...prev, userMessage]);
      
      // Clear input
      setInputMessage('');
      
      // Set loading state
      setIsLoading(true);
      
      let assistantResponse: string;
      
      // Decide whether to use actual API or simulated response
      if (USE_ACTUAL_API) {
        // Convert messages to format expected by API (excluding the welcome message)
        const apiMessages = messages.concat(userMessage);
        
        console.log('Sending message to API:', content);
        console.log('With conversation history:', apiMessages);
        
        // Send message to API
        try {
          const response = await AIService.sendMessage(content, apiMessages);
          assistantResponse = response.data.response;
          console.log('Received API response:', response);
        } catch (apiError: any) {
          console.error('API call failed:', apiError);
          throw new Error(`API call failed: ${apiError.message || 'Unknown error'}`);
        }
      } else {
        // Use simulated response and add a delay to mimic API call
        await new Promise(resolve => setTimeout(resolve, 1000));
        assistantResponse = getSimulatedResponse(content);
      }
      
      // Add AI response to chat
      const assistantMessage: ChatMessage = {
        role: 'assistant',
        content: assistantResponse
      };
      
      setMessages(prev => [...prev, assistantMessage]);
    } catch (err: any) {
      console.error('Error sending message:', err);
      setError(`The AI service is currently unavailable: ${err.message || 'Unknown error'}. Please try again later.`);
    } finally {
      setIsLoading(false);
    }
  };
  
  // Handle prompt card click
  const handlePromptClick = (prompt: string) => {
    setInputMessage(prompt);
    handleSendMessage(prompt);
  };
  
  // Handle clearing the chat
  const handleClearChat = () => {
    setMessages([]);
    setError(null);
    setShowPrompts(true);
  };
  
  // Handle key press in input
  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage(inputMessage);
    }
  };

  // Handle opening a document
  const handleOpenDocument = (filename: string) => {
    // Open the document from the public/documents folder
    const documentUrl = `/documents/${filename}`;
    window.open(documentUrl, '_blank');
  };

  return (
    <div className="min-h-screen pb-8">
      {/* Page Header - Batch 1 */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: 0.1 }}
        className="bg-brand-gradient dark:bg-dark-gradient rounded-brand p-4 sm:p-6 mb-4 sm:mb-6 text-white shadow-brand dark:shadow-dark"
      >
        <h1 className="text-2xl sm:text-3xl font-bold mb-2">ICHRA Knowledge Center</h1>
        <p className="text-sky-100 text-sm sm:text-base">Ask questions about Individual Coverage Health Reimbursement Arrangements</p>
      </motion.div>

      {/* Frequently Referenced Documents Section - Batch 2 */}
      <motion.div
        id="documents-section"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: 0.5 }}
        className="bg-white dark:bg-night-900 rounded-brand shadow-brand dark:shadow-dark overflow-hidden mb-4"
      >
        {/* Header with gradient background matching Kyndly Assistant */}
        <div className="bg-gradient-to-r from-moss to-seafoam dark:from-night-800 dark:to-night-950 px-4 sm:px-6 py-4">
          <motion.button
            onClick={() => setShowDocuments(!showDocuments)}
            className="w-full flex items-center justify-between text-white hover:bg-white/10 rounded-lg px-2 py-1 transition-colors duration-200"
            whileHover={{ scale: 1.01 }}
            whileTap={{ scale: 0.99 }}
          >
            <div className="flex items-center">
              <div className="rounded-full bg-white dark:bg-night-800 w-8 sm:w-10 h-8 sm:h-10 flex items-center justify-center mr-3 shadow-sm">
                <svg className="w-4 sm:w-5 h-4 sm:h-5 text-seafoam dark:text-sky" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <div className="text-left">
                <h3 className="font-semibold text-base sm:text-lg text-white">Frequently Referenced Documents</h3>
                <p className="text-xs text-white/80">Essential ICHRA resources and guides</p>
              </div>
            </div>
            <motion.div
              animate={{ rotate: showDocuments ? 90 : 0 }}
              transition={{ duration: 0.2 }}
              className="text-white/80"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </motion.div>
          </motion.button>
        </div>
        
        <motion.div
          initial={false}
          animate={{
            height: showDocuments ? "auto" : 0,
            opacity: showDocuments ? 1 : 0
          }}
          transition={{ duration: 0.3, ease: "easeInOut" }}
          className="overflow-hidden"
        >
          <motion.div 
            className="px-4 sm:px-6 py-4 bg-gray-50 dark:bg-night-800"
            initial={{ opacity: 0 }}
            animate={{ opacity: showDocuments ? 1 : 0 }}
            transition={{ duration: 0.2, delay: showDocuments ? 0.1 : 0 }}
          >
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {REFERENCE_DOCUMENTS.map((doc, index) => (
                <motion.button
                  key={index}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ 
                    opacity: showDocuments ? 1 : 0, 
                    y: showDocuments ? 0 : 10 
                  }}
                  transition={{ 
                    delay: showDocuments ? 0.2 + (index * 0.1) : 0,
                    duration: 0.3 
                  }}
                  onClick={() => handleOpenDocument(doc.filename)}
                  className="group p-4 bg-white dark:bg-night-700 rounded-lg border border-gray-200 dark:border-night-600 hover:border-seafoam dark:hover:border-sky hover:shadow-md transition-all duration-200 text-left"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <div className="flex items-start">
                    <div className="w-8 h-8 rounded-md bg-red-100 dark:bg-red-900/30 flex items-center justify-center mr-3 flex-shrink-0 group-hover:bg-red-200 dark:group-hover:bg-red-900/50 transition-colors">
                      <svg className="w-4 h-4 text-red-600 dark:text-red-400" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                        <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="font-medium text-sm text-night dark:text-white group-hover:text-seafoam dark:group-hover:text-sky transition-colors line-clamp-2">
                        {doc.name}
                      </h4>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 line-clamp-2">
                        {doc.description}
                      </p>
                      <div className="flex items-center mt-2 text-xs text-seafoam dark:text-sky opacity-0 group-hover:opacity-100 transition-opacity">
                        <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                        </svg>
                        Open PDF
                      </div>
                    </div>
                  </div>
                </motion.button>
              ))}
            </div>
          </motion.div>
        </motion.div>
      </motion.div>

      {/* Chat Section - Batch 1 - Now with better height management */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: 0.3 }}
        className="bg-white dark:bg-night-900 rounded-brand shadow-brand dark:shadow-dark overflow-hidden flex flex-col min-h-[70vh] sm:min-h-[60vh]"
      >
        {/* Chat header */}
        <div className="bg-gradient-to-r from-moss to-seafoam dark:from-night-800 dark:to-night-950 px-4 sm:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center">
            <div className="rounded-full bg-white dark:bg-night-800 w-8 sm:w-10 h-8 sm:h-10 flex items-center justify-center mr-3 shadow-sm">
              <span className="text-seafoam dark:text-sky text-sm sm:text-lg font-bold">K</span>
            </div>
            <div>
              <h2 className="font-semibold text-white text-base sm:text-lg">Kyndly Assistant</h2>
              <p className="text-xs text-white/80">AI ICHRA Expert</p>
            </div>
          </div>
          <button
            onClick={handleClearChat}
            className="text-white hover:text-sky-100 p-2 rounded-full hover:bg-white/10 transition-colors group"
            title="Start new conversation"
          >
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          </button>
        </div>
        
        {/* Error message */}
        {error && (
          <FadeIn>
            <div className="bg-red-50 dark:bg-red-900/20 border-l-4 border-red-400 p-4 mx-4 my-2">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
                </div>
              </div>
            </div>
          </FadeIn>
        )}
        
        {/* Chat messages or prompt cards - Now with flexible height */}
        <div 
          ref={chatContainerRef}
          className="flex-1 px-4 py-4 bg-gray-50 dark:bg-night-800 min-h-0"
        >
          {/* Show prompt cards if no messages and showPrompts is true */}
          {messages.length === 0 && showPrompts ? (
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5 }}
              className="py-4 sm:py-6"
            >
              {/* Welcome message - Batch 1 */}
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1, duration: 0.4 }}
                className="mb-6 sm:mb-8 text-center max-w-2xl mx-auto"
              >
                <div className="mb-4">
                  <div className="w-12 sm:w-16 h-12 sm:h-16 bg-gradient-to-r from-seafoam to-moss dark:from-sky dark:to-seafoam rounded-full flex items-center justify-center mx-auto shadow-md">
                    <svg className="w-6 sm:w-8 h-6 sm:h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                    </svg>
                  </div>
                </div>
                <h3 className="text-xl sm:text-2xl font-semibold text-night dark:text-white mb-3">Welcome to the Kyndly Assistant</h3>
                <p className="text-night-600 dark:text-night-100 text-base sm:text-lg">
                  I'm your ICHRA expert. Ask me anything about Individual Coverage Health Reimbursement Arrangements.
                </p>
              </motion.div>
              
              {/* Main Navigation Pages - Batch 1 */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-5 max-w-4xl mx-auto">
                {PROMPT_CARDS.map((cardGroup, groupIndex) => (
                  <motion.div 
                    key={groupIndex}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 + groupIndex * 0.1, duration: 0.4 }}
                    className="bg-white dark:bg-night-700 p-4 sm:p-5 rounded-brand border border-gray-200 dark:border-night-600 shadow-sm dark:shadow-dark hover:shadow-md transition-shadow"
                  >
                    <div className="flex items-center mb-3">
                      <div className="w-8 sm:w-10 h-8 sm:h-10 rounded-full bg-seafoam/10 dark:bg-sky/10 flex items-center justify-center mr-3">
                        <div className="text-seafoam dark:text-sky">
                          {cardGroup.icon}
                        </div>
                      </div>
                      <div>
                        <h3 className="font-medium text-base sm:text-lg text-seafoam dark:text-sky">{cardGroup.category}</h3>
                        <p className="text-xs text-gray-500 dark:text-gray-400">{cardGroup.description}</p>
                      </div>
                    </div>
                    <div className="space-y-2">
                      {cardGroup.prompts.map((prompt, promptIndex) => (
                        <motion.button
                          key={`${groupIndex}-${promptIndex}`}
                          initial={{ opacity: 0, x: -10 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: 0.4 + groupIndex * 0.1 + promptIndex * 0.05, duration: 0.3 }}
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                          className="w-full text-left p-3 rounded-md bg-gray-50 dark:bg-night-600 hover:bg-seafoam/10 dark:hover:bg-sky/10 text-sm text-night-800 dark:text-white transition-colors border border-gray-200 dark:border-night-500"
                          onClick={() => handlePromptClick(prompt)}
                        >
                          {prompt}
                        </motion.button>
                      ))}
                    </div>
                  </motion.div>
                ))}
              </div>
              
              {!USE_ACTUAL_API && (
                <motion.div 
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.8 }}
                  className="mt-6 text-center text-xs text-gray-400 dark:text-gray-500 italic"
                >
                  Note: This is a preview with simulated responses. Full AI functionality coming soon.
                </motion.div>
              )}
            </motion.div>
          ) : (
            <div className="py-4 space-y-4 max-h-[50vh] sm:max-h-none overflow-y-auto">
              {messages.map((message, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: message.role === "user" ? 20 : -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.1 * index, duration: 0.3 }}
                >
                  <ChatMessageComponent message={message} />
                </motion.div>
              ))}
              {messages.length > 0 && !USE_ACTUAL_API && (
                <div className="text-center text-xs text-gray-400 dark:text-gray-500 italic py-1">
                  Using simulated responses. Full AI integration coming soon.
                </div>
              )}
              {isLoading && (
                <motion.div 
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="flex justify-start"
                >
                  <div className="bg-white dark:bg-night-700 rounded-lg px-4 py-3 max-w-[80%] shadow-sm">
                    <div className="flex items-center space-x-2">
                      <div className="rounded-full bg-seafoam w-6 h-6 flex items-center justify-center">
                        <span className="text-white text-xs font-bold">K</span>
                      </div>
                      <div className="flex space-x-1">
                        <div className="h-2 w-2 bg-seafoam dark:bg-sky rounded-full animate-pulse"></div>
                        <div className="h-2 w-2 bg-seafoam dark:bg-sky rounded-full animate-pulse" style={{ animationDelay: '0.2s' }}></div>
                        <div className="h-2 w-2 bg-seafoam dark:bg-sky rounded-full animate-pulse" style={{ animationDelay: '0.4s' }}></div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              )}
              <div ref={messagesEndRef} />
            </div>
          )}
        </div>
        
        {/* Chat input */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.4 }}
          className="border-t border-gray-200 dark:border-night-700 p-4 bg-white dark:bg-night-900"
        >
          <div className="relative flex items-center bg-gray-50 dark:bg-night-800 rounded-full border border-gray-300 dark:border-night-600 focus-within:border-seafoam dark:focus-within:border-sky focus-within:ring-1 focus-within:ring-seafoam dark:focus-within:ring-sky shadow-sm">
            <textarea
              className="flex-1 resize-none bg-transparent border-0 focus:ring-0 py-3 pl-4 pr-10 min-h-[50px] max-h-32 text-night dark:text-white placeholder-gray-400 dark:placeholder-gray-500 rounded-l-full"
              placeholder="Ask about ICHRA..."
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              onKeyDown={handleKeyPress}
              disabled={isLoading}
              rows={1}
            />
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className={`absolute right-3 rounded-full p-2 ${
                isLoading || !inputMessage.trim()
                  ? 'text-gray-400 dark:text-gray-600 cursor-not-allowed'
                  : 'text-seafoam dark:text-sky hover:bg-seafoam/10 dark:hover:bg-sky/10'
              }`}
              onClick={() => handleSendMessage(inputMessage)}
              disabled={isLoading || !inputMessage.trim()}
              title="Send message"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path d="M10.894 2.553a1 1 0 00-1.788 0l-7 14a1 1 0 001.169 1.409l5-1.429A1 1 0 009 15.571V11a1 1 0 112 0v4.571a1 1 0 00.725.962l5 1.428a1 1 0 001.17-1.408l-7-14z" />
              </svg>
            </motion.button>
          </div>
          <div className="mt-2 text-xs text-center text-gray-500 dark:text-gray-400">
            Press Enter to send, Shift+Enter for new line
          </div>
        </motion.div>
      </motion.div>
    </div>
  );
};

export default KnowledgeCenter; 